import React, { useEffect, useState, useMemo } from 'react';
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalculateIcon from '@mui/icons-material/Calculate';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from 'react-data-grid';
import update from 'immutability-helper';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import _ from 'lodash';

import DefaultApi from '../../Api/DefaultApi';
import {
  rowKeyGetter,
  autoFocusAndSelect,
  handleCopy,
  renderTextEditor,
} from '../../Components/ReactDataGridProps';

const INTEGER_ONLY_COLUMNS = ['MagicNumber'];

function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'Upper':
    case 'Lower':
    case 'HoursPerPayPeriod':
      return (a, b) => {
        return a[sortColumn] - b[sortColumn];
      };
    case 'OrgId':
    case 'OrgName':
      return (a, b) => {
        return a[sortColumn].localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function OrgTable({ comRow }) {
  const [orgRows, setOrgRows] = useState([]);
  const [selectedOrgRow, setSelectedOrgRow] = useState(null);
  const [orgSortColumns, setOrgSortColumns] = useState([]);
  const [chargeOutRates, setChargeOutRates] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('');

  useEffect(() => {
    const asyncFn = () => {
      getOrganisations(comRow.id);
    };
    asyncFn();
  }, [comRow]);

  const getOrganisations = (com_id) => {
    DefaultApi.getOrganisations(com_id).then((res) => {
      if (res.status === 200) setOrgRows(res.data);
      else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };

  const onOrgClickedAdd = () => {
    DefaultApi
      .addOrganisation(comRow.id, comRow.FiscalYearStartMonth)
      .then((res) => {
        if (res.status === 200) getOrganisations(comRow.id);
        else toast.error(res.messgae);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleOrgRowChange = (updatedRows, field) => {
    const columnKey = field.column.key;
    const rowIndex = field.indexes[0];
    const newValue = updatedRows[rowIndex][columnKey];

    if (INTEGER_ONLY_COLUMNS.includes(columnKey)) {
      const parsed = Number(newValue);
      if (!Number.isInteger(parsed)) {
        toast.warning('Average Rate Per Hour must be an integer');
        setOrgRows([...orgRows]);
        return;
      }
      updatedRows = update(updatedRows, {
        [rowIndex]: { [columnKey]: { $set: parsed } },
      });
    }

    setOrgRows(updatedRows);
    let updated_data = {
      rowIds: [updatedRows[rowIndex].id],
      updated: {
        [columnKey]: updatedRows[rowIndex][columnKey],
      },
    };
    DefaultApi.updateOrganisationData(updated_data).then((res) => {
      if (res.status === 200) toast.success(res.message);
      else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };

  const handleOrgCellClick = (rowData) => {
    if (selectedOrgRow?.id !== rowData.row.id) {
      setSelectedOrgRow(rowData.row);
      getMagicData(rowData.row);
    }
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = ['id', 'MagicNumber', 'IsActive'];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const onOrgClickedDelete = () => {
    if (!selectedOrgRow) return;

    DefaultApi
      .deleteOrganisation(selectedOrgRow.id)
      .then((res) => {
        if (res.status === 200) {
          getOrganisations(comRow.id);
          setSelectedOrgRow(null);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const getMagicData = (orgRow) => {
    if (orgRow.ChargeOutRates) {
      setChargeOutRates(JSON.parse(orgRow.ChargeOutRates));
    } else {
      setChargeOutRates({});
    }
  };

  const handleChargeRate = (event) => {
    let charge_out_rates = Object.assign({}, chargeOutRates);
    charge_out_rates[event.target.name] = event.target.value
      ? Number(event.target.value)
      : '';
    if (
      event.target.name === 'operatingCost' ||
      event.target.name === 'employmentExpenses'
    ) {
      charge_out_rates.totalExpense = charge_out_rates.operatingCost
        ? charge_out_rates.operatingCost
        : 0;
      charge_out_rates.totalExpense += charge_out_rates.employmentExpenses
        ? charge_out_rates.employmentExpenses
        : 0;
    }

    if (
      event.target.name === 'hoursBillable' ||
      event.target.name === 'operatingCost' ||
      event.target.name === 'employmentExpenses'
    ) {
      if (charge_out_rates.hoursBillable) {
        charge_out_rates.costRate =
          charge_out_rates.totalExpense / charge_out_rates.hoursBillable;
        charge_out_rates.averateChargeOutRate = charge_out_rates.costRate * 1.2;
        charge_out_rates.costRate =
          Math.round((charge_out_rates.costRate + Number.EPSILON) * 100) / 100;
        charge_out_rates.averateChargeOutRate =
          Math.round(
            (charge_out_rates.averateChargeOutRate + Number.EPSILON) * 100,
          ) / 100;
      }
    }

    if (event.target.name === 'hoursBillable' || event.target.name === 'fees') {
      if (charge_out_rates.hoursBillable && charge_out_rates.fees) {
        charge_out_rates.feesPerBillableHour =
          charge_out_rates.fees / charge_out_rates.hoursBillable;
        charge_out_rates.feesPerBillableHour =
          Math.round(
            (charge_out_rates.feesPerBillableHour + Number.EPSILON) * 100,
          ) / 100;
      }
    }

    if (
      event.target.name === 'hoursBillable' ||
      event.target.name === 'averageBillableTime'
    ) {
      if (charge_out_rates.averageBillableTime && charge_out_rates.hoursBillable) {
        charge_out_rates.amountOfBillable =
          charge_out_rates.hoursBillable /
          charge_out_rates.averageBillableTime;
        charge_out_rates.amountOfBillable =
          Math.round(
            (charge_out_rates.amountOfBillable + Number.EPSILON) * 100,
          ) / 100;
      }
    }

    setChargeOutRates(charge_out_rates);
  };

  const handleModalClose = () => {
    if (Object.keys(chargeOutRates).length > 0) {
      let rowIndex = _.findIndex(orgRows, {
        id: selectedOrgRow.id,
      });
      let updated_data = {
        rowIds: [selectedOrgRow.id],
        updated: {
          ChargeOutRates: JSON.stringify(chargeOutRates),
        },
      };
      let updatedRows = update(orgRows, {
        [rowIndex]: { $merge: updated_data.updated },
      });
      
      DefaultApi.updateOrganisationData(updated_data).then((res) => {
        if (res.status === 200) {
          setOrgRows(updatedRows);
        } else toast.error(res.message);
      }).catch((error) => toast.error(error.message));
    }

    setModalOpen(false);
    setModalMode('');
  };

  const orgColumns = useMemo(() => {
    return [
      {
        key: 'id',
        name: 'ID',
        sortable: true,
        resizable: true,
        width: 40,
      },
      {
        key: 'OrgId',
        name: 'OrgId',
        sortable: true,
        resizable: true,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'OrgName',
        name: 'OrgName',
        sortable: true,
        resizable: true,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Upper',
        name: 'Upper',
        sortable: true,
        resizable: true,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Upper}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Upper: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'Lower',
        name: 'Lower',
        sortable: true,
        resizable: true,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Lower}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Lower: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'MagicNumber',
        name: 'Average Rate Per Hour',
        resizable: true,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.MagicNumber}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                MagicNumber: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
        renderCell: (props) => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {props.row.MagicNumber}
            <CalculateIcon
              style={{
                color: 'white',
                cursor: 'pointer',
                backgroundColor: 'green',
                width: 'fit-content',
                margin: '0 auto',
              }}
              onClick={() => {
                setModalOpen(true);
                setModalMode('magic');
              }}
            />
          </div>
        ),
      },
      {
        key: 'HoursPerPayPeriod',
        name: 'HoursPerPayPeriod',
        sortable: true,
        resizable: true,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.HoursPerPayPeriod}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                HoursPerPayPeriod: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'IsActive',
        name: 'Active',
        renderCell: (props) => (
          <Checkbox
            checked={props.row.IsActive !== null ? props.row.IsActive : false}
            onChange={(e) => {
              e.preventDefault();
              props.onRowChange(
                { ...props.row, IsActive: !props.row.IsActive },
                true,
              );
            }}
            color="success"
          ></Checkbox>
        ),
      },
    ];
    // eslint-disable-next-line
  }, [orgRows]);

  const sortedRows = useMemo(() => {
    if (orgSortColumns.length === 0) return orgRows;

    return [...orgRows].sort((a, b) => {
      for (const sort of orgSortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [orgRows, orgSortColumns]);
  
  return (
    <>
      <div style={{ display: 'flex', width: '100%' }}>
        <div style={{ flex: 1 }} />
        <IconButton aria-label="Add" onClick={onOrgClickedAdd}>
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={onOrgClickedDelete}>
          <DeleteIcon />
        </IconButton>
      </div>

      <DataGrid
        rowKeyGetter={rowKeyGetter}
        className="fill-grid rdg-light"
        columns={orgColumns}
        rows={sortedRows}
        headerRowHeight={40}
        rowClass={(row, index) =>
          index % 2 === 1 ? 'rdg-highlight-cell' : undefined
        }
        sortColumns={orgSortColumns}
        onSortColumnsChange={setOrgSortColumns}
        onRowsChange={handleOrgRowChange}
        onCellClick={handleOrgCellClick}
        onCopy={handleCopy}
        onPaste={handlePaste}
        rowHeight={60}
        style={{ height: window.innerHeight / 2 - 100 }}
      />

      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={modalOpen && modalMode === 'magic'}
        onClose={handleModalClose}
      >
        <DialogTitle>Charge Out Rates</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleModalClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Hours Billable"
                name="hoursBillable"
                value={
                  chargeOutRates.hoursBillable
                    ? chargeOutRates.hoursBillable
                    : ''
                }
                onChange={handleChargeRate}
                type="number"
                variant="filled"
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Based on FTE"
                name="basedOnFte"
                value={
                  chargeOutRates.basedOnFte ? chargeOutRates.basedOnFte : ''
                }
                onChange={handleChargeRate}
                type="number"
                variant="filled"
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Operating Cost"
                name="operatingCost"
                value={
                  chargeOutRates.operatingCost
                    ? chargeOutRates.operatingCost
                    : ''
                }
                onChange={handleChargeRate}
                type="number"
                variant="filled"
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Employment Expenses"
                name="employmentExpenses"
                value={
                  chargeOutRates.employmentExpenses
                    ? chargeOutRates.employmentExpenses
                    : ''
                }
                onChange={handleChargeRate}
                type="number"
                variant="filled"
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Total Expense"
                name="totalExpense"
                value={
                  chargeOutRates.totalExpense ? chargeOutRates.totalExpense : ''
                }
                type="number"
                variant="filled"
                disabled
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Fees"
                name="fees"
                value={chargeOutRates.fees ? chargeOutRates.fees : ''}
                onChange={handleChargeRate}
                type="number"
                variant="filled"
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Cost Rate"
                name="costRate"
                value={chargeOutRates.costRate ? chargeOutRates.costRate : ''}
                type="number"
                variant="filled"
                disabled
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Average charge out rate 20% profit margin"
                name="averateChargeOutRate"
                value={
                  chargeOutRates.averateChargeOutRate
                    ? chargeOutRates.averateChargeOutRate
                    : ''
                }
                type="number"
                variant="filled"
                disabled
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Fee per billable hour"
                name="feesPerBillableHour"
                value={
                  chargeOutRates.feesPerBillableHour
                    ? chargeOutRates.feesPerBillableHour
                    : ''
                }
                type="number"
                variant="filled"
                disabled
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Average billable time per person each year at 80% recoverable"
                helperText="Average billable time per person each year at 80% recoverable"
                name="averageBillableTime"
                value={
                  chargeOutRates.averageBillableTime
                    ? chargeOutRates.averageBillableTime
                    : ''
                }
                onChange={handleChargeRate}
                type="number"
                variant="filled"
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <TextField
                id="filled-helperText"
                label="Amount of billable people based on hours worked"
                helperText="Amount of billable people based on hours worked"
                name="amountOfBillable"
                value={
                  chargeOutRates.amountOfBillable
                    ? chargeOutRates.amountOfBillable
                    : ''
                }
                type="number"
                variant="filled"
                disabled
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontStyle: 'italic',
                  textAlign: 'center',
                  mt: 2,
                  p: 2,
                  backgroundColor: 'grey.100',
                  borderRadius: 1
                }}
              >
                Note: This is just a calculator. It will not insert values into the fields.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}

OrgTable.propTypes = {
  comRow: PropTypes.object.isRequired,
}

export default OrgTable;
